import {
  configureStore,
  combineReducers,
} from '@reduxjs/toolkit';
import { thunk } from 'redux-thunk';

import {
  userLoginReducers,
  userRegisterReducers,
  userDetailsReducers,
  userUpdateProfileReducers,
  userUpdateProfileImageReducers,
  userFollowReducers,
  userUnfollowReducers,
  userListReducers,
  userDeleteReducers,
  userUpdateReducers,
  userSendMailReducers,
  validateUserProfileReducers,
  userSendMailChangePassReducers,
  changePasswordUserReducers
} from './reducers/userReducers';
import {
  chatCreateReducers,
} from "./reducers/chatReducers";
import chatReducer from "./reducers/chatReducers";

// Phase 1 scaffolding slices — populated in Phase 2 via synchronous
// preloadedState projection from the legacy localStorage.userInfo blob.
// Source of truth still lives in state.userLogin.userInfo (Phase 6 inverts).
import { authReducers } from './reducers/authReducers';
import { profileReducers } from './reducers/profileReducers';
import { bootstrapReducers } from './reducers/bootstrapReducers';
import {
  projectAuthFromLegacy,
  projectProfileFromLegacy,
} from './persistence/legacyProjection';
import { getProductionAuthPersistence } from './persistence/authPersistenceProduction';

// Phase 6 — first controlled extraction. Additive slice for the
// notifications feature. Direct reducer import (not the feature barrel) to
// keep the store's module graph minimal — same pattern as existing reducers.
import notificationReducer from './features/notifications/model/notificationReducer';
import presenceReducer from './features/presence/model/presenceReducer';


const reducer = combineReducers({
  userLogin: userLoginReducers,
  userRegister: userRegisterReducers,
  userDetails: userDetailsReducers,
  userUpdateProfile: userUpdateProfileReducers,
  userUpdateProfileImage: userUpdateProfileImageReducers,
  userFollow: userFollowReducers,
  userUnfollow: userUnfollowReducers,
  userList: userListReducers,
  userDelete: userDeleteReducers,
  userUpdate: userUpdateReducers,
  userSendMail: userSendMailReducers,
  validateUserProfile: validateUserProfileReducers,
  userSendMailChangePass: userSendMailChangePassReducers,
  changePasswordUser: changePasswordUserReducers,

  chat: chatReducer,
  chatCreate: chatCreateReducers,

  // ---- Phase 1 scaffolding (inert placeholders; not consumed yet) ----
  auth: authReducers,
  profile: profileReducers,
  bootstrap: bootstrapReducers,

  // ---- Phase 6 — first controlled extraction (notifications) ----
  notifications: notificationReducer,

  // ---- USER-PRESENCE-A1 — ephemeral realtime presence ----
  presence: presenceReducer,
});

// Synchronous read of the legacy userInfo blob via auth persistence facade.
// Corrupt/non-object payloads are cleared best-effort and treated as absent.
const userInfoFromStorage = getProductionAuthPersistence().readUserInfoBlob();

// Phase 2: project the same legacy blob into the new auth + profile slices
// SYNCHRONOUSLY at preloadedState time. Guarantees no API request, no WS
// connect, and no Phase 5+ consumer ever observes empty auth on cold start
// when localStorage holds tokens. bootstrap.phase = 'hydrating' iff the
// bootstrapApp() thunk will actually run (i.e. the App component is mounted).
const initialState = {
  userLogin: { userInfo: userInfoFromStorage },
  auth: projectAuthFromLegacy(userInfoFromStorage),
  profile: projectProfileFromLegacy(userInfoFromStorage),
  bootstrap: { phase: 'hydrating' },
};

const middleware = [thunk];

const store = configureStore({
  reducer: reducer,
  preloadedState: initialState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: middleware,
      },
    }),
});

export default store;